import { Controller } from "@hotwired/stimulus"
import { autoUpdate, computePosition, flip, shift } from "@floating-ui/dom"

// The composer's @mention typeahead.
//
// Anchored to the caret, not to the field — so it needs a Floating UI *virtual*
// element built from the current Range's client rect, the same virtual-reference
// shape the block menu already uses. It cannot reuse components/select, which is a
// field-anchored panel with a different shape and different keyboard contract.
//
// `candidates` is only the content's own people, filtered here as you type. Anyone
// else comes from `searchUrl` once the query is long enough — the full user list is
// far too large to ship into the page.
const SEARCH_MIN_LENGTH = 2
const SEARCH_DELAY_MS = 150

export default class extends Controller {
  static targets = ["body", "panelTemplate", "rowTemplate"]
  static values = { candidates: Array, searchUrl: String }

  disconnect() {
    this.cancelSearch()
    this.close()
  }

  // The @ button in the footer: insert the trigger character, then open.
  trigger(event) {
    event.preventDefault()
    this.bodyTarget.focus()
    document.execCommand("insertText", false, "@")
    this.onInput()
  }

  onInput() {
    const query = this.activeQuery()

    if (query === null) {
      this.cancelSearch()
      this.close()
      return
    }

    this.open(query)
    this.scheduleSearch(query)
  }

  onKeydown(event) {
    if (!this.panel) return

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      this.moveActive(event.key === "ArrowDown" ? 1 : -1)
      return
    }

    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      event.stopPropagation()
      this.selectActive()
      return
    }

    // Escape dismisses the typeahead only — the composer stays open.
    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.cancelSearch()
      this.close()
    }
  }

  // ---- Query ----------------------------------------------------------------

  // The text between the nearest unconsumed "@" and the caret. Null when the caret
  // is not inside a mention token at all.
  activeQuery() {
    const selection = window.getSelection()
    if (!selection?.rangeCount) return null

    const range = selection.getRangeAt(0)
    if (range.startContainer.nodeType !== Node.TEXT_NODE) return null

    const before = range.startContainer.textContent.slice(0, range.startOffset)
    const match = before.match(/@([^\s@]*)$/)

    return match ? match[1] : null
  }

  matches(query) {
    const needle = query.toLowerCase()

    return this.candidatesValue.filter((candidate) => {
      return candidate.name.toLowerCase().includes(needle) || candidate.email.toLowerCase().includes(needle)
    })
  }

  // ---- Search ---------------------------------------------------------------

  scheduleSearch(query) {
    this.cancelSearch()
    if (!this.hasSearchUrlValue || query.length < SEARCH_MIN_LENGTH) return

    this.searchTimer = setTimeout(() => this.search(query), SEARCH_DELAY_MS)
  }

  async search(query) {
    this.searchRequest = new AbortController()
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("query", query)

    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.searchRequest.signal
      })
      if (!response.ok) return

      const { candidates } = await response.json()
      // The caret moved on while this was in flight; its own search will answer.
      if (this.activeQuery() !== query) return

      // The server already leaves the team out; this only guards row ids should the
      // page's team list have gone stale.
      const teamIds = new Set(this.candidatesValue.map((candidate) => String(candidate.id)))
      const others = candidates.filter((candidate) => !teamIds.has(String(candidate.id)))
      if (others.length === 0) return

      // Team rows keep their positions, so the row the arrow keys reached stays active.
      this.open(query, { others, keepActive: true })
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  cancelSearch() {
    clearTimeout(this.searchTimer)
    this.searchRequest?.abort()
    this.searchRequest = null
  }

  // ---- Panel ----------------------------------------------------------------

  open(query, { others = [], keepActive = false } = {}) {
    const results = [...this.matches(query), ...others]

    // Nothing to choose from is not a panel — an empty popover is just a stray box.
    if (results.length === 0) {
      this.close()
      return
    }

    this.ensurePanel()
    this.renderRows(results)
    this.setActive(keepActive ? this.activeIndex ?? 0 : 0)
    this.reposition()
  }

  ensurePanel() {
    if (this.panel) return

    this.panel = this.panelTemplateTarget.content.firstElementChild.cloneNode(true)
    document.body.appendChild(this.panel)
    this.bodyTarget.setAttribute("aria-expanded", "true")
  }

  renderRows(results) {
    this.panel.replaceChildren()

    results.forEach((candidate, index) => {
      const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true)

      row.id = `course-review-mention-${candidate.id}`
      row.dataset.userId = candidate.id
      row.dataset.name = candidate.name
      row.dataset.index = index
      row.querySelector(".course-review-mention-option__avatar").textContent = candidate.initial
      row.querySelector(".course-review-mention-option__name").textContent = candidate.name
      row.querySelector(".course-review-mention-option__email").textContent = candidate.email
      // Only the author and reviewers carry a role label; everyone else has none.
      const tag = row.querySelector(".course-review-mention-option__tag")
      tag.textContent = candidate.tag || ""
      tag.hidden = !candidate.tag
      row.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.select(row)
      })

      this.panel.appendChild(row)
    })
  }

  rows() {
    return Array.from(this.panel?.children || [])
  }

  setActive(index) {
    const rows = this.rows()
    if (rows.length === 0) return

    this.activeIndex = (index + rows.length) % rows.length

    rows.forEach((row, position) => {
      const active = position === this.activeIndex
      row.classList.toggle("is-active", active)
      row.setAttribute("aria-selected", active ? "true" : "false")
      if (active) {
        this.bodyTarget.setAttribute("aria-activedescendant", row.id)
        row.scrollIntoView({ block: "nearest" })
      }
    })
  }

  moveActive(step) {
    this.setActive((this.activeIndex ?? 0) + step)
  }

  selectActive() {
    const row = this.rows()[this.activeIndex ?? 0]
    if (row) this.select(row)
  }

  // ---- Insertion ------------------------------------------------------------

  // Replaces the typed "@query" with an atomic, non-editable span so a single
  // backspace removes the whole token rather than eating it one letter at a time.
  select(row) {
    const selection = window.getSelection()
    if (!selection?.rangeCount) return

    const range = selection.getRangeAt(0)
    const textNode = range.startContainer
    const before = textNode.textContent.slice(0, range.startOffset)
    const match = before.match(/@([^\s@]*)$/)
    if (!match) return

    const start = range.startOffset - match[0].length
    const replacement = document.createRange()
    replacement.setStart(textNode, start)
    replacement.setEnd(textNode, range.startOffset)
    replacement.deleteContents()

    const token = document.createElement("span")
    token.className = "course-review-mention"
    token.contentEditable = "false"
    token.dataset.userId = row.dataset.userId
    token.textContent = `@${row.dataset.name}`

    const spacer = document.createTextNode(" ")
    replacement.insertNode(spacer)
    replacement.insertNode(token)

    const after = document.createRange()
    after.setStartAfter(spacer)
    after.collapse(true)
    selection.removeAllRanges()
    selection.addRange(after)

    this.cancelSearch()
    this.close()
    this.bodyTarget.focus()
    this.dispatch("selected", { detail: { userId: row.dataset.userId } })
  }

  // ---- Positioning ----------------------------------------------------------

  reposition() {
    this.cleanup?.()

    const reference = this.caretReference()
    const update = () => {
      computePosition(reference, this.panel, {
        placement: "bottom-start",
        strategy: "fixed",
        middleware: [flip(), shift({ padding: 8 })]
      }).then(({ x, y }) => {
        if (!this.panel) return
        Object.assign(this.panel.style, { left: `${x}px`, top: `${y}px` })
      })
    }

    update()
    this.cleanup = autoUpdate(reference, this.panel, update)
  }

  // A virtual element at the caret. A collapsed Range can report a zero rect in
  // some positions, so it falls back to the body's own rect.
  caretReference() {
    return {
      contextElement: this.bodyTarget,
      getBoundingClientRect: () => {
        const selection = window.getSelection()

        if (selection?.rangeCount) {
          const rect = selection.getRangeAt(0).getBoundingClientRect()
          if (rect.width || rect.height || rect.top) return rect
        }

        return this.bodyTarget.getBoundingClientRect()
      }
    }
  }

  close() {
    this.cleanup?.()
    this.cleanup = null
    this.panel?.remove()
    this.panel = null
    this.activeIndex = null
    this.bodyTarget?.removeAttribute("aria-activedescendant")
    this.bodyTarget?.setAttribute("aria-expanded", "false")
  }
}
